import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import Co2Icon from "@mui/icons-material/Co2";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import SpaIcon from "@mui/icons-material/Spa";
import GrainIcon from "@mui/icons-material/Grain";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import AirIcon from "@mui/icons-material/Air";
import { SectionHeading } from "../Common/Typography/AppTypography";
import ParameterCard from "./ParameterCard";
import AqiSummaryCard from "./AqiSummaryCard";
import NearestMonitorMapCard from "./NearestMonitorMapCard";

const IndoorDashboard = ({
  indoor,
  lat,
  lon,
  roomName,
  lastUpdatedLabel,
}) => {
  // Reference order: PM2.5 → PM10 → PM1 → Temperature → Humidity → Noise → TVOC → CO₂
  const indoorParams = [
    {
      name: "PM2.5",
      value: indoor?.pm25,
      unit: "µg/m³",
      rangeKey: "pm25",
      icon: <GrainIcon fontSize="small" />,
    },
    {
      name: "PM10",
      value: indoor?.pm10,
      unit: "µg/m³",
      rangeKey: "pm10",
      icon: <BlurOnIcon fontSize="small" />,
    },
    {
      name: "PM1",
      value: indoor?.pm1,
      unit: "µg/m³",
      rangeKey: "pm1",
      icon: <AirIcon fontSize="small" />,
    },
    {
      name: "Temperature",
      value: indoor?.temperatureC,
      unit: "°C",
      rangeKey: "temperature",
      icon: <ThermostatIcon fontSize="small" />,
      info: "Indoor temperature in Celsius",
    },
    {
      name: "Humidity",
      value: indoor?.humidity,
      unit: "%",
      rangeKey: "humidity",
      icon: <WaterDropIcon fontSize="small" />,
    },
    {
      name: "Noise",
      value: indoor?.noise,
      unit: "dB",
      rangeKey: "noise",
      icon: <GraphicEqIcon fontSize="small" />,
    },
    {
      name: "TVOC",
      value: indoor?.tvoc,
      unit: "ppm",
      rangeKey: "tvoc",
      icon: <SpaIcon fontSize="small" />,
    },
    {
      name: "CO₂",
      value: indoor?.co2,
      unit: "ppm",
      rangeKey: "co2",
      icon: <Co2Icon fontSize="small" />,
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <SectionHeading sx={{ mb: 0.5 }}>Indoor</SectionHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Indoor space environmental parameters for the selected space.
      </Typography>

      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <AqiSummaryCard
            aqiValue={indoor?.aqiIn}
            lastUpdatedLabel={lastUpdatedLabel}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <NearestMonitorMapCard
            lat={lat}
            lon={lon}
            roomName={roomName}
            roomAqi={indoor?.aqiIn}
            height={220}
          />
        </Grid2>
      </Grid2>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Indoor Space Parameters
      </Typography>
      <Grid2 container spacing={1.5}>
        {indoorParams.map((param) => (
          <Grid2
            key={`${param.name}-${param.unit}`}
            size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
          >
            <ParameterCard {...param} />
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
};

IndoorDashboard.propTypes = {
  indoor: PropTypes.object,
  lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  roomName: PropTypes.string,
  lastUpdatedLabel: PropTypes.string,
};

export default IndoorDashboard;
